/**
 * One markdown pipeline for the short user prose the app stores outside
 * documents — notes and quotes.
 *
 * Documents are already markdown (TipTap round-trips it, the slide editor
 * parses it), and notes and quotes are inserted verbatim into documents, so
 * what a user types in a note has to mean the same thing in both places.
 * That is why this goes through `marked` rather than a second hand-rolled
 * subset: two dialects of markdown in one app is worse than none.
 *
 * Two levels, because the two things are not the same shape:
 *
 *   `renderMarkdown`       Full blocks, for notes. A note is prose — headings,
 *                          lists and blockquotes all earn their place.
 *   `renderInlineMarkdown` Emphasis only, for quotes. A quote is a passage
 *                          with an attribution; a heading or a bullet list
 *                          inside a pull-quote reads as noise, and it breaks
 *                          the `> ` wrapping applied when a quote is inserted
 *                          into a document.
 *
 * Both sanitise on the way out. The content comes back from the API and is
 * handed to `dangerouslySetInnerHTML`, so the allowlist below is the thing
 * standing between a stored note and script execution.
 */

import DOMPurify from "dompurify";
import { Marked } from "marked";
import { footnoteTokenizer } from "@/components/editor/extensions/footnote-markdown";

/**
 * Tags a note may produce. Deliberately no `img`, `iframe` or `input`: notes
 * are typed in a textarea, and nothing in that flow needs to embed anything.
 */
const BLOCK_TAGS = [
  "p", "br", "hr", "span",
  "strong", "em", "del", "code", "pre",
  "blockquote",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "ul", "ol", "li",
  "a", "sup",
  "table", "thead", "tbody", "tr", "th", "td",
];

/** Tags a quote may produce — the inline set, plus a break for wrapped lines. */
const INLINE_TAGS = ["strong", "em", "del", "code", "a", "br", "sup"];

const ALLOWED_ATTR = ["href", "title", "target", "rel", "align", "start", "class"];

/**
 * Links open in a new tab: a note is a workspace, and following a reference
 * should not discard what the user was reading. `noopener` because the opened
 * page is arbitrary.
 */
const renderer = {
  link(this: any, { href, title, tokens }: any) {
    const text = this.parser.parseInline(tokens);
    const attrs = title ? ` title="${title.replace(/"/g, "&quot;")}"` : "";
    return `<a href="${href}"${attrs} target="_blank" rel="noopener noreferrer">${text}</a>`;
  },
};

/**
 * Footnotes render as a numbered marker carrying the note in its tooltip.
 *
 * Anywhere markdown is displayed rather than edited, the note is apparatus —
 * showing `^[a long citation]` mid-sentence is worse than not showing it at
 * all. Without this the raw `^[…]` would simply appear as text, which is what
 * the preview page used to do.
 *
 * `footnoteTokenizer` is the same scanner the editor's highlighter and
 * `collectFootnotes` use, so all three agree on where a footnote starts and
 * ends. Its `tokenize` is renamed here because `marked`'s extension API calls
 * the same function `tokenizer`.
 */
const footnoteExtension = {
  name: footnoteTokenizer.name,
  level: footnoteTokenizer.level,
  start: footnoteTokenizer.start,
  tokenizer: (src: string) => footnoteTokenizer.tokenize(src),
  renderer: (token: { note?: string }) =>
    `<sup class="footnote-ref" title="${String(token.note ?? "").replace(/"/g, "&quot;")}"></sup>`,
};

/**
 * A private `marked` instance, so nothing that reaches for the global one can
 * change how notes render underneath them. (The old slide editor did exactly
 * that, with `marked.setOptions`; it is gone, but the isolation is still the
 * right default.)
 *
 * `breaks: true` matters more here than it does for documents: notes are
 * written in a plain textarea where a single Return is meant as a line break,
 * not as a paragraph continuation.
 */
const md = new Marked({ gfm: true, breaks: true, renderer });
md.use({ extensions: [footnoteExtension] });

export function renderMarkdown(text: string): string {
  if (!text) return "";
  return DOMPurify.sanitize(md.parse(text, { async: false }) as string, {
    ALLOWED_TAGS: BLOCK_TAGS,
    ALLOWED_ATTR,
  });
}

export function renderInlineMarkdown(text: string): string {
  if (!text) return "";
  return DOMPurify.sanitize(md.parseInline(text, { async: false }) as string, {
    ALLOWED_TAGS: INLINE_TAGS,
    ALLOWED_ATTR,
  });
}

/**
 * Wrap text as a markdown blockquote for insertion into a document.
 *
 * Every line needs the marker: prefixing only the first one leaves the rest as
 * an ordinary paragraph, which is what a multi-line quote used to do. Blank
 * lines get a bare `>` so the quote stays one block rather than splitting into
 * two.
 */
export function blockquote(text: string): string {
  return text
    .trim()
    .split("\n")
    .map((line) => (line.trim() ? `> ${line}` : ">"))
    .join("\n");
}

/**
 * The markdown with its syntax resolved away, for the places that show a
 * one-line preview — table cells, the command palette, dashboard lists.
 *
 * Rendering blocks into a truncated cell produces a mess, and showing the raw
 * source means a `## ` in front of every heading, so neither alternative works
 * where there is only room for a line. Parsing and then taking the text is
 * more reliable than stripping syntax with regexes.
 */
export function stripMarkdown(text: string): string {
  if (!text) return "";
  const doc = new DOMParser().parseFromString(renderMarkdown(text), "text/html");
  return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
}
